import { useCallback, useRef } from 'react'
import { useAuth } from '../utils/Auth/AuthProvider'
import { aiBatchGenerate } from '../utils/remote'
import { repairLatexJson } from '../utils/repairLatexJson'

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

export interface GeminiChapterResult {
    temp_idx: number
    chaporsubid: number
    is_subchapter: boolean
}

export interface GeminiExplanationResult {
    question_idx: number
    explanation: string
    references: string[]
    corrected_answer: string
    insufficient_context?: boolean
    determined_course?: string | null
    chapter_name?: string | null
    chapter_id?: number | null
}

export interface GeminiBookSelectionResult {
    question_idx: number
    book_ids: number[]
}

function extractJson(text: string): any {
    // Strip markdown code blocks if present
    const mdMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/)
    if (mdMatch) text = mdMatch[1]
    const start = text.indexOf('[')
    const end = text.lastIndexOf(']')
    const jsonStr = start >= 0 && end > start ? text.slice(start, end + 1) : text
    try {
        // Explanations carry LaTeX with single backslashes (\frac, \rightarrow), which
        // JSON.parse rejects or silently turns into control characters.
        return JSON.parse(repairLatexJson(jsonStr))
    } catch (e) {
        console.error('Failed to parse JSON from Gemini text:', text)
        throw e
    }
}

const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = error => reject(error)
    })
}

const urlToBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(blob)
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = error => reject(error)
    })
}

export function useGeminiClient(_geminiKey: string | null) {
    const { token } = useAuth()
    const embeddingCacheRef = useRef<Map<string, number[]>>(new Map())

    const assignChaptersBatch = useCallback(async (
        questions: Array<{ temp_idx: number; question: string; choices: string[] }>,
        catalog: any[]
    ): Promise<GeminiChapterResult[]> => {
        const prompt = (
            `You are given an array of questions and a catalog of chapters/subchapters. Each catalog entry includes a "course_name" indicating which course it belongs to.\n` +
            `Catalog:\n${JSON.stringify(catalog)}\n\n` +
            `For each question in the input array, choose the single best chapter or subchapter from the catalog.\n` +
            `CRITICAL RULES:\n` +
            `1. You MUST only use "id" values that exist in the provided catalog for the "chaporsubid" field.\n` +
            `2. Match the "is_subchapter" flag exactly as it appears in the catalog for the chosen entry.\n` +
            `3. PAY ATTENTION TO THE COURSE CONTEXT: If similar chapter/subchapter names exist under different courses (e.g. "Trauma" under "Surgery" vs "Trauma" under "Gynecology"), choose the one under the most clinically appropriate course for the question content.\n` +
            `4. Return a strict JSON array of objects with keys: temp_idx, chaporsubid, is_subchapter. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.\n` +
            `5. CRITICAL: Use the EXACT "temp_idx" values provided in the input. Do NOT re-index them to 0, 1, 2, etc.\n` +
            `Questions:\n${JSON.stringify(questions)}`
        )

        const res = await aiBatchGenerate({
            toBeValidated: token,
            messages: [{ role: 'user', content: prompt }],
            temperature: 0.1, // Low temperature for classification
        })

        const outText = res.text
        if (!outText) throw new Error('Empty Gemini response')
        const parsed = extractJson(outText)
        if (!Array.isArray(parsed)) throw new Error('Invalid response format')
        
        const originalIndices = questions.map(q => q.temp_idx)
        const returnedIndices = (parsed as any[]).map(item => item.temp_idx)
        const mismatch = returnedIndices.some(idx => !originalIndices.includes(idx))
        const usePositionalMapping = mismatch && parsed.length === questions.length

        return parsed.map((item: any, idx: number) => ({
            temp_idx: usePositionalMapping ? originalIndices[idx] : item.temp_idx,
            chaporsubid: item.chaporsubid,
            is_subchapter: !!item.is_subchapter,
        })) as GeminiChapterResult[]
    }, [token])

    const selectBooksBatch = useCallback(async (
        questions: Array<{ question_idx: number; question: string; choices: string[] }>,
        books: Array<{ id: number; title: string; author?: string | null; course_names?: string[]; subject?: string | null; grade?: string | null }>
    ): Promise<GeminiBookSelectionResult[]> => {
        const prompt = (
            `You are given an array of multiple-choice questions and a catalog of textbooks.\n` +
            `For each question, select the TOP 3 books from the catalog that are MOST LIKELY to contain material relevant to answering the question.\n\n` +
            `Catalog (each book has id, title, optional author, course_names, subject, grade):\n${JSON.stringify(books)}\n\n` +
            `Questions:\n${JSON.stringify(questions)}\n\n` +
            `RULES:\n` +
            `1. For each question, return EXACTLY 3 book IDs (or all available if catalog has fewer than 3 books).\n` +
            `2. Use ONLY "id" values that exist in the provided catalog.\n` +
            `3. Match by subject matter, course context, and conceptual fit between question content and book topic. Title and course_names are the strongest signals.\n` +
            `4. Order book_ids from MOST relevant (first) to LEAST relevant.\n` +
            `5. Return a STRICT JSON array of objects with keys: question_idx, book_ids. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json.\n\n` +
            `6. CRITICAL: Use the EXACT "question_idx" values provided in the input. Do NOT re-index them to 0, 1, 2, etc.\n\n` +
            `Each output object MUST contain:\n` +
            `{\n  "question_idx": number,\n  "book_ids": [int, int, int]\n}`
        )

        const res = await aiBatchGenerate({
            toBeValidated: token,
            messages: [{ role: 'user', content: prompt }],
            temperature: 0.1,
        })

        const outText = res.text
        if (!outText) throw new Error('Empty Gemini response')
        const parsed = extractJson(outText)
        if (!Array.isArray(parsed)) throw new Error('Invalid response format')

        const originalIndices = questions.map(q => q.question_idx)
        const returnedIndices = (parsed as any[]).map(item => item.question_idx)
        const mismatch = returnedIndices.some(idx => !originalIndices.includes(idx))
        const usePositionalMapping = mismatch && parsed.length === questions.length

        return (parsed as any[])
            .filter((r) => r && Array.isArray(r.book_ids))
            .map((r, idx) => ({
                question_idx: usePositionalMapping ? originalIndices[idx] : r.question_idx,
                book_ids: r.book_ids.map((b: any) => Number(b)).filter((n: number) => Number.isFinite(n)),
            }))
    }, [token])

    const generateExplanationsBatch = useCallback(async (
        batchPayload: Array<{
            question_idx: number
            question: string
            choices: any[]
            student_answer: string
            hits: any[]
        }>,
        courseNames: string[],
        imageQuestions?: Array<{ idx: number; file?: File; imageUrl?: string }>
    ): Promise<GeminiExplanationResult[]> => {
        const results: GeminiExplanationResult[] = []

        // Separate image and non-image questions
        const imageIdxSet = new Set((imageQuestions || []).map(iq => iq.idx))
        const nonImageItems = batchPayload.filter(p => !imageIdxSet.has(p.question_idx))
        const imageItems = batchPayload.filter(p => imageIdxSet.has(p.question_idx))

        // Process non-image questions in batch
        if (nonImageItems.length > 0) {
            const coursePriorityInstruction = courseNames.length
                ? `\nCourse prioritization: the following courses are in-scope: ${JSON.stringify(courseNames)}. For each question, first output a field "determined_course" with the chosen course name (one of the list) or null if none. When producing explanation and references, prioritize hits coming from book(s) associated with the determined_course.`
                : ''

            const prompt = `You are given an array of multiple-choice question objects (JSON). For each question, examine the provided FAISS hits (snippets, page numbers, book names and chapter info) and return a detailed, evidence-backed explanation that directly answers the question.${coursePriorityInstruction}

Rules and formatting (READ CAREFULLY):
- You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
- CRITICAL: Use the EXACT "question_idx" values provided in the input. Do NOT re-index them to 0, 1, 2, etc.
- Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
- Each output object MUST contain these keys exactly:
    {
        "question_idx": number,
        "explanation": string,
        "references": [string],
        "corrected_answer": string, // Set to the choice label (A, B, C, or D) if the answer is being corrected
        "insufficient_context": boolean
    }

Corrected Answer rules:
- To determine the correct answer, you MUST use both the provided text context (FAISS hits) and your own internal academic/medical knowledge base.
- PRIORITIZE the provided text context: if the textbooks provided in the hits contain information to answer the question, use that as your primary source.
- SUPPLEMENT with your own knowledge: use your internal knowledge to confirm the context's findings, fill in gaps, or provide a more complete explanation if the context is limited.
- YOU MUST ALWAYS set "corrected_answer" to the single letter (A, B, C, D, or E) representing the correct choice, even if the "student_answer" provided is already correct.
- NEVER return the full text of the answer in "corrected_answer", only the single letter label (A, B, C, D, or E).

Explanation rules:
- The explanation MUST directly and thoroughly answer the question.
- Structure the explanation as follows:
    1. A comprehensive clinical/academic explanation of the core concept and reasoning.
    2. A "Choice Analysis" section (using <b> or similar HTML) explaining in detail why EACH individual choice (A, B, C, D, E) is either correct or incorrect. You MUST address every single option provided.
    3. A final concluding sentence explaining why the right answer is the correct choice.
- Do NOT include any meta-commentary about textbooks, sources, or their relevance. Only provide the actual medical/scientific answer.
- Use HTML tags like <b>, <br>, or <ul> for structured readability within the JSON string.

Evidence rules:
- You MUST provide evidence for your explanation using the provided text snippets (FAISS hits).
- MANDATORY EXCERPTS: For EVERY reference you include, you MUST provide a short, verbatim excerpt of the most relevant sentence(s) that support your answer. 
- FORMAT: Use the "With excerpt" format as the standard. Only use the "Without excerpt" format as a rare fallback if a hit is cited for general background context and truly contains no specific sentence that can be quoted.
- Each reference MUST use this EXACT HTML format:
    - With excerpt: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\""
    - Without excerpt: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>"
- Do NOT invent page numbers, book names, or make up direct quotes. Only use what is provided in the hits.
- Only set "insufficient_context" to true if NONE of the provided hits are even remotely related to the question. If you include at least one reference (with or without an excerpt), "insufficient_context" MUST be false.
- If "insufficient_context" is true, STILL provide a COMPLETE explanation using your own knowledge. Do NOT describe what the context lacks.
- If it is maths or physics question, show your work and use LaTeX for formulas. Format explanation with simple HTML where helpful.

Input (array):
${JSON.stringify(nonImageItems)}

Respond with the STRICT JSON array matching the schema above.`

            const res = await aiBatchGenerate({
                toBeValidated: token,
                messages: [{ role: 'user', content: prompt }],
            })

            const outText = res.text
            if (outText) {
                try {
                    const parsed = extractJson(outText)
                    if (Array.isArray(parsed)) {
                        const originalIndices = nonImageItems.map(p => p.question_idx)
                        const returnedIndices = parsed.map(item => item.question_idx)
                        
                        // Check if the AI re-indexed the results (e.g. 0, 1, 2...)
                        // If the length matches and the returned indices don't match the original set, 
                        // we fallback to mapping by array position.
                        const mismatch = returnedIndices.some(idx => !originalIndices.includes(idx))
                        const usePositionalMapping = mismatch && parsed.length === nonImageItems.length

                        parsed.forEach((item, idx) => {
                            const q_idx = usePositionalMapping ? originalIndices[idx] : item.question_idx
                            results.push({
                                question_idx: q_idx,
                                explanation: String(item?.explanation ?? ''),
                                references: Array.isArray(item?.references) ? item.references : (item?.references ? [String(item.references)] : []),
                                corrected_answer: String(item?.corrected_answer ?? ''),
                                insufficient_context: !!item?.insufficient_context,
                                determined_course: item?.determined_course ?? null,
                                chapter_name: item?.chapter_name ?? null,
                                chapter_id: item?.chapter_id ?? null,
                            })
                        })
                    }
                } catch (e) {
                    console.error('Error parsing batch explanations:', e)
                }
            }
        }

        // Process image questions one by one
        for (const imgItem of imageItems) {
            const imgInfo = (imageQuestions || []).find(iq => iq.idx === imgItem.question_idx)
            if (!imgInfo) continue

            const singlePayload = [imgItem]
            const singlePrompt = `You are given a multiple-choice question with an image. Examine the provided FAISS hits and return a detailed, evidence-backed explanation that directly answers the question. Do NOT include meta-commentary about textbooks or their relevance — only provide the actual clinical/academic answer with detailed reasoning.

Rules and formatting (READ CAREFULLY):
- You MUST respond with STRICT JSON only. The top-level response must be a JSON array with one object for each input question, in the same order.
- Do NOT write any text outside the JSON array. No commentary, no numbering, no markdown.
- Each output object MUST contain these keys exactly:
    {
        "question_idx": number,
        "explanation": string,
        "references": [string],
        "corrected_answer": string, // Set to the choice label (A, B, C, or D) if the answer is being corrected
        "insufficient_context": boolean
    }

Corrected Answer rules:
- To determine the correct answer, use both the provided text context and your own internal knowledge.
- PRIORITIZE the provided text context if it contains relevant info.
- YOU MUST ALWAYS set "corrected_answer" to the single letter (A, B, C, D, or E) representing the correct choice, even if the "student_answer" provided is already correct.
- NEVER return the full text of the answer in "corrected_answer", only the single letter label (A, B, C, D, or E).

Explanation rules:
- Directly and thoroughly answer the question based on the image and context.
- Structure the explanation as follows:
    1. A comprehensive clinical/academic explanation of the core concept and reasoning.
    2. A "Choice Analysis" section (using <b> or similar HTML) explaining in detail why EACH individual choice (A, B, C, D, E) is either correct or incorrect. You MUST address every single option provided.
    3. A final concluding sentence explaining why the right answer is the correct choice.
- Do NOT include any meta-commentary about sources.
- Use HTML for structured readability (e.g. <b>, <br>).

Evidence rules:
- You MUST provide evidence for your explanation using the provided FAISS hits.
- MANDATORY EXCERPTS: For EVERY reference you include, you MUST provide a short, verbatim excerpt of the most relevant sentence(s) that support your answer.
- FORMAT: Use the "With excerpt" format as the standard. Only use the "Without excerpt" format as a rare fallback if a hit is cited for general background context and truly contains no specific sentence that can be quoted.
- Each reference MUST use this EXACT HTML format:
    - With excerpt: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>: \"...excerpt...\""
    - Without excerpt: "<span style=\"color:#0081B9\">{BookName} - {ChapterName} - </span> <span style=\"color:#49c6fc\">page {page_no}</span>"
- Only set "insufficient_context" to true if NONE of the provided hits are even remotely related to the question. If you include at least one reference (with or without an excerpt), "insufficient_context" MUST be false.
- If "insufficient_context" is true, STILL provide a COMPLETE explanation using your own knowledge. Do NOT describe what the context lacks — just answer the question fully.

Input:
${JSON.stringify(singlePayload)}

Respond with STRICT JSON array.`

            let base64Data = ''
            let mimeType = 'image/png'

            try {
                if (imgInfo.file) {
                    base64Data = await fileToBase64(imgInfo.file)
                    mimeType = imgInfo.file.type
                } else if (imgInfo.imageUrl) {
                    base64Data = await urlToBase64(imgInfo.imageUrl)
                }
            } catch (e) {
                console.error('Failed to convert image to base64:', e)
            }

            try {
                const res = await aiBatchGenerate({
                    toBeValidated: token,
                    messages: [{ role: 'user', content: singlePrompt }],
                    images: base64Data ? [{ mime_type: mimeType, data: base64Data }] : undefined
                })

                const outText = res.text
                if (outText) {
                    const parsed = extractJson(outText)
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        const item = parsed[0]
                        results.push({
                            question_idx: item.question_idx ?? imgItem.question_idx,
                            explanation: String(item?.explanation ?? ''),
                            references: Array.isArray(item?.references) ? item.references : [],
                            corrected_answer: String(item?.corrected_answer ?? ''),
                            insufficient_context: !!item?.insufficient_context,
                            determined_course: item?.determined_course ?? null,
                        })
                    }
                }
            } catch (e) {
                console.error(`Error processing image question ${imgItem.question_idx}:`, e)
            }
        }

        return results
    }, [token])

    const generateFromKnowledge = useCallback(async (
        questions: Array<{ question_idx: number; question: string; choices: any[]; student_answer: string }>
    ): Promise<GeminiExplanationResult[]> => {
        const prompt = `You are given multiple-choice questions. Answer each using your own knowledge base (no external context provided).

Return a STRICT JSON array. Return ONLY valid JSON. Do not include any introductory text, concluding text, or markdown formatting like \`\`\`json. Each object:
{
    "question_idx": number,
    "explanation": string,
    "references": [],
    "corrected_answer": string, // Set to the choice label (A, B, C, D, or E) if the answer is being corrected
    "insufficient_context": false
}

Corrected Answer rules:
- YOU MUST ALWAYS set "corrected_answer" to the single letter (A, B, C, D, or E) representing the correct choice, even if the "student_answer" provided is already correct.
- NEVER return the full text of the answer in "corrected_answer", only the single letter label (A, B, C, D, or E).

Explanation rules:
- Directly and thoroughly answer the question.
- Structure the explanation as follows:
    1. A comprehensive clinical/academic explanation of the core concept and reasoning.
    2. A "Choice Analysis" section (using <b> or similar HTML) explaining in detail why EACH individual choice (A, B, C, D, E) is either correct or incorrect. You MUST address every single option provided.
    3. A final concluding sentence explaining why the right answer is the correct choice.
- Do NOT include any meta-commentary about textbooks, sources, or their relevance. Only provide the actual medical/scientific answer.
- Use HTML for structured readability (e.g. <b>, <br>).

Input:
${JSON.stringify(questions)}

Respond with STRICT JSON array only.`

        const res = await aiBatchGenerate({
            toBeValidated: token,
            messages: [{ role: 'user', content: prompt }],
        })

        const outText = res.text
        if (!outText) return []
        const parsed = extractJson(outText)
        if (!Array.isArray(parsed)) return []
        return parsed.map((item: any) => ({
            question_idx: item.question_idx,
            explanation: String(item?.explanation ?? ''),
            references: Array.isArray(item?.references) ? item.references : [],
            corrected_answer: String(item?.corrected_answer ?? ''),
            insufficient_context: false,
        }))
    }, [token])

    const getEmbedding = useCallback(async (text: string, token: string | null): Promise<number[]> => {
        const cached = embeddingCacheRef.current.get(text)
        if (cached) return cached
        const { aiEmbedContent } = await import('../utils/remote')
        const embData = await aiEmbedContent({
            toBeValidated: token,
            contents: [text],
            model: 'gemini-embedding-001'
        })
        const embedding = embData?.embeddings?.[0]?.values
        if (!embedding) throw new Error('No embedding returned')
        embeddingCacheRef.current.set(text, embedding)
        return embedding
    }, [])

    return {
        assignChaptersBatch,
        selectBooksBatch,
        generateExplanationsBatch,
        generateFromKnowledge,
        getEmbedding,
        stripHtml,
    }
}
